/** 口座情報入力のバリデーション（BankInfoValidator と整合） */
const BANK_CODE_RE = /^\d{4}$/;
const BANK_BRANCH_CODE_RE = /^\d{3}$/;
const BANK_ACCOUNT_NUMBER_RE = /^\d{1,7}$/;
const BANK_ACCOUNT_HOLDER_RE = /^[\uFF61-\uFF9F\u0020]+$/;

export const digitsOnly = (value: unknown) => String(value ?? '').replace(/\D/g, '');

/** 口座名義用：全角カタカナ・ひらがな・全角スペース → 半角カタカナ・半角スペースへ自動変換（申出フォームと同仕様） */
const KANA_FULL_TO_HALF: Record<string, string> = {
  'ガ':'ｶﾞ','ギ':'ｷﾞ','グ':'ｸﾞ','ゲ':'ｹﾞ','ゴ':'ｺﾞ','ザ':'ｻﾞ','ジ':'ｼﾞ','ズ':'ｽﾞ','ゼ':'ｾﾞ','ゾ':'ｿﾞ',
  'ダ':'ﾀﾞ','ヂ':'ﾁﾞ','ヅ':'ﾂﾞ','デ':'ﾃﾞ','ド':'ﾄﾞ','バ':'ﾊﾞ','ビ':'ﾋﾞ','ブ':'ﾌﾞ','ベ':'ﾍﾞ','ボ':'ﾎﾞ',
  'パ':'ﾊﾟ','ピ':'ﾋﾟ','プ':'ﾌﾟ','ペ':'ﾍﾟ','ポ':'ﾎﾟ','ヴ':'ｳﾞ','ヷ':'ﾜﾞ','ヺ':'ｦﾞ',
  'ア':'ｱ','イ':'ｲ','ウ':'ｳ','エ':'ｴ','オ':'ｵ','カ':'ｶ','キ':'ｷ','ク':'ｸ','ケ':'ｹ','コ':'ｺ',
  'サ':'ｻ','シ':'ｼ','ス':'ｽ','セ':'ｾ','ソ':'ｿ','タ':'ﾀ','チ':'ﾁ','ツ':'ﾂ','テ':'ﾃ','ト':'ﾄ',
  'ナ':'ﾅ','ニ':'ﾆ','ヌ':'ﾇ','ネ':'ﾈ','ノ':'ﾉ','ハ':'ﾊ','ヒ':'ﾋ','フ':'ﾌ','ヘ':'ﾍ','ホ':'ﾎ',
  'マ':'ﾏ','ミ':'ﾐ','ム':'ﾑ','メ':'ﾒ','モ':'ﾓ','ヤ':'ﾔ','ユ':'ﾕ','ヨ':'ﾖ',
  'ラ':'ﾗ','リ':'ﾘ','ル':'ﾙ','レ':'ﾚ','ロ':'ﾛ','ワ':'ﾜ','ヲ':'ｦ','ン':'ﾝ',
  'ァ':'ｧ','ィ':'ｨ','ゥ':'ｩ','ェ':'ｪ','ォ':'ｫ','ッ':'ｯ','ャ':'ｬ','ュ':'ｭ','ョ':'ｮ',
  'ー':'ｰ','・':'･','　':' ',
};

export const toHalfKana = (value: unknown) =>
  String(value ?? '')
    .replace(/[ぁ-ゖ]/g, (c) => String.fromCharCode(c.charCodeAt(0) + 0x60)) // ひらがな→カタカナ
    .replace(/[ァ-ヺー・\u3000]/g, (c) => KANA_FULL_TO_HALF[c] ?? c);            // 全角カナ・記号→半角

export const sanitizeBankAccountHolderInput = (value: string) => toHalfKana(value);

export const sanitizeBankCodeInput = (value: string) => digitsOnly(value).slice(0, 4);

export const sanitizeBankBranchCodeInput = (value: string) => digitsOnly(value).slice(0, 3);

export const sanitizeBankAccountNumberInput = (value: string) => digitsOnly(value).slice(0, 7);

export const bankCodeError = (value: unknown) => {
  const bankCode = String(value ?? '').trim();
  if (!bankCode) return '銀行番号を入力してください。';
  if (!BANK_CODE_RE.test(bankCode)) return '半角数字4桁で入力してください。';
  return '';
};

export const bankBranchCodeError = (value: unknown) => {
  const branchCode = String(value ?? '').trim();
  if (!branchCode) return '支店番号を入力してください。';
  if (!BANK_BRANCH_CODE_RE.test(branchCode)) return '半角数字3桁で入力してください。';
  return '';
};

export const bankAccountNumberError = (value: unknown) => {
  const accountNumber = String(value ?? '').trim();
  if (!accountNumber) return '口座番号を入力してください。';
  if (!BANK_ACCOUNT_NUMBER_RE.test(accountNumber)) return '半角数字1〜7桁で入力してください。';
  return '';
};

export const bankAccountHolderKanaError = (value: unknown) => {
  const holder = String(value ?? '').trim();
  if (!holder) return '口座名義（ｶﾀｶﾅ）を入力してください。';
  if (!BANK_ACCOUNT_HOLDER_RE.test(holder)) {
    return '半角ｶﾀｶﾅ、半角スペースのみで入力してください。';
  }
  return '';
};

export const sanitizeBankFieldInput = (key: string, value: string) => {
  switch (key) {
    case 'bankCode':
    case 'bankName':
      return sanitizeBankCodeInput(value);
    case 'bankBranchCode':
    case 'branchName':
      return sanitizeBankBranchCodeInput(value);
    case 'accountNumber':
    case 'bankAccountNumber':
      return sanitizeBankAccountNumberInput(value);
    case 'accountHolderKana':
      return sanitizeBankAccountHolderInput(value);
    default:
      return value;
  }
};
